/**
 * ProcessRepositoryPanel Component
 * Tree view of process repository with drag-and-drop to canvas
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  ChevronDown,
  Search,
  RefreshCw,
  Check,
  Pencil,
  Plus,
  AlertTriangle,
  Link2,
  GitBranch,
  ListFilter,
  Trash2,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  useProcessRepository,
  type ProcessTreeNode,
  flattenTree,
} from '../hooks/useProcessRepository';
import type { CompassProcessImpact } from '../../compass/models/ProcessImpact';
import { useBoundRecordIds } from '../hooks/useBoundRecordIds';
import { ImportSourceModal } from '../../../core/data-profiles/components';
import { useGraphBoundProcessIds } from '../hooks/useGraphBoundProcessIds';
import { useEffectiveRecordGetter } from '../../data-binding/hooks/useBoundRecordStore';
import {
  NoRepositoryConnected,
  RepositoryEmpty,
  AllItemsOnCanvas,
  NoSearchResults,
  RepositoryError,
  RepositoryLoading,
} from '../../data-binding/components/RepositoryEmptyStates';
import { RepositoryPanel } from '@/core/data-profiles/components/RepositoryPanel';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';

/**
 * Change status for a record in the repository view
 */
export type RecordChangeStatus = 'modified' | 'new' | 'conflict' | 'deleted' | null;

type RepositoryChangeFacet = Exclude<RecordChangeStatus, null>;

interface ChangeFilterCounts {
  changed: number;
  new: number;
  modified: number;
  conflict: number;
  deleted: number;
}

export interface DeletedRepositoryRecord {
  recordId: string;
  data: Record<string, unknown>;
  source: 'local' | 'remote';
}

/**
 * Map of recordId to change status
 */
export type RecordChangeMap = Map<string, RecordChangeStatus>;

interface ProcessRepositoryPanelProps {
  sourceId: string | undefined;
  folderId?: string;
  onSelectProcess?: (processId: string, processData: Record<string, unknown>) => void;
  onDragStart?: (processId: string, processData: Record<string, unknown>) => void;
  onConnectRepository?: () => void;
  /** Map of record IDs to their change status (for diff highlighting) */
  recordChanges?: RecordChangeMap;
  /** Records that are pending deletion or were found deleted remotely. */
  deletedRecords?: DeletedRepositoryRecord[];
  className?: string;
}

const emptyChangeCounts: ChangeFilterCounts = {
  changed: 0,
  new: 0,
  modified: 0,
  conflict: 0,
  deleted: 0,
};

const changeFilterFacets: RepositoryChangeFacet[] = [
  'new',
  'modified',
  'conflict',
  'deleted',
];

const matchesChangeFilters = (
  status: RecordChangeStatus,
  filters: ReadonlySet<RepositoryChangeFacet>,
): boolean => {
  return status !== null && filters.has(status);
};

const flattenTreeByChangeFilters = (
  nodes: ProcessTreeNode[],
  statuses: Map<string, RecordChangeStatus>,
  filters: ReadonlySet<RepositoryChangeFacet>,
): ProcessTreeNode[] => {
  if (filters.size === 0) return [];

  const result: ProcessTreeNode[] = [];
  const traverse = (node: ProcessTreeNode) => {
    if (matchesChangeFilters(statuses.get(node.record.id) ?? null, filters)) {
      result.push(node);
    }
    node.children.forEach(traverse);
  };

  nodes.forEach(traverse);
  return result;
};

const readRecordName = (
  data: Record<string, unknown>,
  fallback: string,
): string => {
  const value = data.Name ?? data.name ?? data.title ?? data.label;
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
};

const recordMatchesText = (
  record: DeletedRepositoryRecord,
  filterText: string,
): boolean => {
  const normalized = filterText.trim().toLowerCase();
  if (normalized.length === 0) return true;
  const name = readRecordName(record.data, record.recordId).toLowerCase();
  return name.includes(normalized) || record.recordId.toLowerCase().includes(normalized);
};

export const ProcessRepositoryPanel: React.FC<ProcessRepositoryPanelProps> = ({
  sourceId,
  folderId,
  onSelectProcess,
  onDragStart,
  onConnectRepository,
  recordChanges,
  deletedRecords = [],
  className,
}) => {
  const { t } = useTranslation('process');
  const [searchText, setSearchText] = useState('');
  const [activeChangeFilters, setActiveChangeFilters] = useState<Set<RepositoryChangeFacet>>(
    () => new Set()
  );
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);
  const authenticatedFetch = useAuthenticatedFetch();

  const { tree, isLoading, error, refetch, totalCount, records } = useProcessRepository({
    sourceId,
    folderId,
    filterText: searchText,
  });
  const isGraphBackedView = !sourceId && !!folderId;

  const processImpactQuery = useQuery<{ impact: CompassProcessImpact }>({
    queryKey: ['compass', 'process-impact', folderId ?? '', selectedProcessId ?? ''],
    enabled: isGraphBackedView && !!folderId && !!selectedProcessId,
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/compass/folders/${folderId}/process-repository/${selectedProcessId}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch process impact');
      }
      return response.json();
    },
    staleTime: 0,
    gcTime: 0,
  });

  const boundRecordIds = useBoundRecordIds(sourceId);
  const graphBoundProcessIds = useGraphBoundProcessIds(folderId);

  // Get effective record values (with local changes merged)
  const getEffectiveRecord = useEffectiveRecordGetter(sourceId);

  // Auto-expand first two levels on load
  React.useEffect(() => {
    if (tree.length > 0 && expandedIds.size === 0) {
      const initialExpanded = new Set<string>();
      tree.forEach((node) => {
        initialExpanded.add(node.record.id);
        node.children.forEach((child) => {
          initialExpanded.add(child.record.id);
        });
      });
      setExpandedIds(initialExpanded);
    }
  }, [tree, expandedIds.size]);

  // Get flattened list for rendering
  const flattenedNodes = useMemo(() => {
    return flattenTree(tree, expandedIds);
  }, [tree, expandedIds]);

  const changeStatusByRecordId = useMemo(() => {
    const next = new Map<string, RecordChangeStatus>();
    recordChanges?.forEach((status, recordId) => {
      if (status) next.set(recordId, status);
    });
    deletedRecords.forEach((record) => {
      next.set(record.recordId, 'deleted');
    });
    return next;
  }, [recordChanges, deletedRecords]);

  const changeCounts = useMemo(() => {
    const counts = { ...emptyChangeCounts };
    changeStatusByRecordId.forEach((status) => {
      if (!status) return;
      counts.changed++;
      counts[status]++;
    });
    return counts;
  }, [changeStatusByRecordId]);

  const clearChangeFilters = useCallback(() => {
    setActiveChangeFilters(new Set());
  }, []);

  const selectAllChangeFilters = useCallback(() => {
    setActiveChangeFilters(new Set(changeFilterFacets));
  }, []);

  const toggleChangeFilter = useCallback((filter: RepositoryChangeFacet) => {
    setActiveChangeFilters((previous) => {
      const next = new Set(previous);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  }, []);

  const visibleNodes = useMemo(() => {
    if (activeChangeFilters.size === 0) return flattenedNodes;
    return flattenTreeByChangeFilters(tree, changeStatusByRecordId, activeChangeFilters);
  }, [activeChangeFilters, flattenedNodes, tree, changeStatusByRecordId]);

  const repositoryRecordIds = useMemo(() => {
    return new Set(records.map((record) => record.id));
  }, [records]);

  const visibleDeletedRecords = useMemo(() => {
    if (!activeChangeFilters.has('deleted')) return [];
    return deletedRecords.filter(
      (record) =>
        !repositoryRecordIds.has(record.recordId) &&
        recordMatchesText(record, searchText)
    );
  }, [activeChangeFilters, deletedRecords, repositoryRecordIds, searchText]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDragStart = useCallback(
    (e: React.DragEvent, node: ProcessTreeNode) => {
      // Get effective data (with local changes applied from central store)
      const effectiveData = getEffectiveRecord(node.record.id, node.record.data);
      // Keep original data separate for cachedRecord (used for change detection)
      const originalData = node.record.data;
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          type: isGraphBackedView ? 'process-graph-item' : 'process-repository-item',
          sourceId,
          folderId,
          recordId: node.record.id,
          data: effectiveData, // For visual display (name, etc.)
          originalData, // For cachedRecord (change detection baseline)
        })
      );
      onDragStart?.(node.record.id, effectiveData);
    },
    [sourceId, folderId, isGraphBackedView, onDragStart, getEffectiveRecord]
  );

  // Count items not yet on canvas
  const itemsNotOnCanvas = useMemo(() => {
    const activeIds = isGraphBackedView ? graphBoundProcessIds : boundRecordIds;
    return records.filter((r) => !activeIds.has(r.id)).length;
  }, [records, boundRecordIds, graphBoundProcessIds, isGraphBackedView]);

  // Empty state: no source connected
  if (!sourceId && !folderId) {
    return (
      <NoRepositoryConnected
        onConnect={onConnectRepository}
        className={className}
        domain="flowchart"
      />
    );
  }

  // Loading state
  if (isLoading) {
    return <RepositoryLoading className={className} domain="flowchart" />;
  }

  // Error state
  if (error) {
    return (
      <RepositoryError
        error={error instanceof Error ? error.message : undefined}
        onRetry={() => refetch()}
        className={className}
        domain="flowchart"
      />
    );
  }

  // Empty repository state
  if (records.length === 0 && deletedRecords.length === 0) {
    return (
      <>
        <RepositoryEmpty
          onRefresh={() => refetch()}
          onAddSource={folderId ? () => setImportModalOpen(true) : undefined}
          className={className}
          domain="flowchart"
        />
        {folderId && (
          <ImportSourceModal
            open={importModalOpen}
            onOpenChange={setImportModalOpen}
            folderId={folderId}
            folderKind="process"
            defaultTarget="folder"
            title={t('repository.addSourceTitle')}
            onComplete={() => {
              setImportModalOpen(false);
              refetch();
            }}
          />
        )}
      </>
    );
  }

  // All items already on canvas (when not searching)
  if (
    !searchText &&
    itemsNotOnCanvas === 0 &&
    changeCounts.changed === 0 &&
    activeChangeFilters.size === 0
  ) {
    return (
      <RepositoryPanel
        className={className}
        header={(
          <SearchHeader
            searchText={searchText}
            onSearchChange={setSearchText}
            onRefresh={refetch}
            totalCount={totalCount}
            activeFilters={activeChangeFilters}
            onClearFilters={clearChangeFilters}
            onSelectAllFilters={selectAllChangeFilters}
            onToggleFilter={toggleChangeFilter}
            changeCounts={changeCounts}
          />
        )}
        body={(
          <AllItemsOnCanvas
            itemCount={records.length}
            onRefresh={() => refetch()}
            domain="flowchart"
          />
        )}
      />
    );
  }

  // No search results
  if (activeChangeFilters.size === 0 && searchText && flattenedNodes.length === 0) {
    return (
      <RepositoryPanel
        className={className}
        header={(
          <SearchHeader
            searchText={searchText}
            onSearchChange={setSearchText}
            onRefresh={refetch}
            totalCount={totalCount}
            activeFilters={activeChangeFilters}
            onClearFilters={clearChangeFilters}
            onSelectAllFilters={selectAllChangeFilters}
            onToggleFilter={toggleChangeFilter}
            changeCounts={changeCounts}
          />
        )}
        body={(
          <NoSearchResults
            searchText={searchText}
            onClearSearch={() => setSearchText('')}
            domain="flowchart"
          />
        )}
      />
    );
  }

  return (
    <RepositoryPanel
      className={className}
      header={(
        <SearchHeader
          searchText={searchText}
          onSearchChange={setSearchText}
          onRefresh={refetch}
          totalCount={totalCount}
          activeFilters={activeChangeFilters}
          onClearFilters={clearChangeFilters}
          onSelectAllFilters={selectAllChangeFilters}
          onToggleFilter={toggleChangeFilter}
          changeCounts={changeCounts}
        />
      )}
      body={(
        // `min-h-0` is mandatory: a flex child's default
        // `min-height: auto` resolves to content height, which lets
        // the ScrollArea grow past its parent and the inner
        // overflow:auto becomes meaningless. With `min-h-0` the
        // scroll viewport correctly clips and renders a scrollbar.
        <ScrollArea className="flex-1 min-h-0">
          <div className="py-1">
            {visibleDeletedRecords.map((record) => (
              <DeletedRepositoryItem
                key={`deleted-${record.recordId}`}
                record={record}
              />
            ))}
            {visibleNodes.map((node) => {
              const effectiveData = getEffectiveRecord(node.record.id, node.record.data);
              return (
                <ProcessTreeItem
                  key={node.record.id}
                  node={node}
                  effectiveData={effectiveData}
                  isExpanded={expandedIds.has(node.record.id)}
                  isOnCanvas={
                    isGraphBackedView
                      ? graphBoundProcessIds.has(node.record.id)
                      : boundRecordIds.has(node.record.id)
                  }
                  changeStatus={changeStatusByRecordId.get(node.record.id) ?? null}
                  onToggle={() => toggleExpanded(node.record.id)}
                  onSelect={() => {
                    setSelectedProcessId(node.record.id);
                    onSelectProcess?.(node.record.id, effectiveData);
                  }}
                  onDragStart={(e) => handleDragStart(e, node)}
                  readOnly={isGraphBackedView}
                />
              );
            })}
            {visibleDeletedRecords.length === 0 && visibleNodes.length === 0 && (
              <ChangeFilterEmptyState
                activeFilters={activeChangeFilters}
                searchText={searchText}
                onShowAll={clearChangeFilters}
              />
            )}
          </div>
          {isGraphBackedView && selectedProcessId && processImpactQuery.data?.impact && (
            <ProcessImpactPanel impact={processImpactQuery.data.impact} />
          )}
        </ScrollArea>
      )}
    />
  );
};

interface SearchHeaderProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  onRefresh: () => void;
  totalCount: number;
  activeFilters: ReadonlySet<RepositoryChangeFacet>;
  onClearFilters: () => void;
  onSelectAllFilters: () => void;
  onToggleFilter: (filter: RepositoryChangeFacet) => void;
  changeCounts: ChangeFilterCounts;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchText,
  onSearchChange,
  onRefresh,
  totalCount,
  activeFilters,
  onClearFilters,
  onSelectAllFilters,
  onToggleFilter,
  changeCounts,
}) => {
  const { t } = useTranslation('process');
  const filters: Array<{
    id: RepositoryChangeFacet;
    label: string;
    count: number;
    icon?: React.ReactNode;
  }> = [
    {
      id: 'new',
      label: t('repository.filters.new', { defaultValue: 'New' }),
      count: changeCounts.new,
      icon: <Plus className="h-3 w-3" />,
    },
    {
      id: 'modified',
      label: t('repository.filters.modified', { defaultValue: 'Modified' }),
      count: changeCounts.modified,
      icon: <Pencil className="h-3 w-3" />,
    },
    {
      id: 'conflict',
      label: t('repository.filters.conflict', { defaultValue: 'Conflicts' }),
      count: changeCounts.conflict,
      icon: <AlertTriangle className="h-3 w-3" />,
    },
    {
      id: 'deleted',
      label: t('repository.filters.deleted', { defaultValue: 'Deleted' }),
      count: changeCounts.deleted,
      icon: <Trash2 className="h-3 w-3" />,
    },
  ];

  return (
    <div className="p-2 border-b border-border space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('repository.searchPlaceholder')}
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onRefresh()}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('repository.refreshTooltip')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {t('repository.processCount', { count: totalCount })}
        </span>
        <RepositoryFilterDropdown
          filters={filters}
          totalCount={totalCount}
          changedCount={changeCounts.changed}
          activeFilters={activeFilters}
          onClearFilters={onClearFilters}
          onSelectAllFilters={onSelectAllFilters}
          onToggleFilter={onToggleFilter}
        />
      </div>
    </div>
  );
};

interface RepositoryFilterOption {
  id: RepositoryChangeFacet;
  label: string;
  count: number;
  icon?: React.ReactNode;
}

interface RepositoryFilterDropdownProps {
  filters: RepositoryFilterOption[];
  totalCount: number;
  changedCount: number;
  activeFilters: ReadonlySet<RepositoryChangeFacet>;
  onClearFilters: () => void;
  onSelectAllFilters: () => void;
  onToggleFilter: (filter: RepositoryChangeFacet) => void;
}

const RepositoryFilterDropdown: React.FC<RepositoryFilterDropdownProps> = ({
  filters,
  totalCount,
  changedCount,
  activeFilters,
  onClearFilters,
  onSelectAllFilters,
  onToggleFilter,
}) => {
  const { t } = useTranslation('process');
  const allChangesSelected = filters.every((filter) => activeFilters.has(filter.id));
  const selectedFilters = filters.filter((filter) => activeFilters.has(filter.id));
  const activeCount =
    activeFilters.size === 0
      ? totalCount
      : selectedFilters.reduce((sum, filter) => sum + filter.count, 0);
  const summary =
    activeFilters.size === 0
      ? t('repository.filters.all', { defaultValue: 'All' })
      : allChangesSelected
        ? t('repository.filters.changed', { defaultValue: 'Changed' })
        : selectedFilters.length === 1
          ? selectedFilters[0].label
          : t('repository.filters.multiple', {
              count: selectedFilters.length,
              defaultValue: `${selectedFilters.length} filters`,
            });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 max-w-[178px] justify-start gap-1.5 px-2 text-xs font-medium"
          aria-label={t('repository.filters.activeLabel', {
            filter: summary,
            count: activeCount,
            defaultValue: `Repository filters: ${summary} (${activeCount})`,
          })}
        >
          <ListFilter className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 truncate">{summary}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {activeCount}
          </span>
          <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">
          {t('repository.filters.label', { defaultValue: 'Repository change filters' })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={activeFilters.size === 0}
          onCheckedChange={onClearFilters}
          onSelect={(event) => event.preventDefault()}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate">
              {t('repository.filters.all', { defaultValue: 'All' })}
            </span>
          </span>
          <span className="ml-auto shrink-0 tabular-nums text-xs text-muted-foreground">
            {totalCount}
          </span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={allChangesSelected}
          onCheckedChange={(checked) => {
            if (checked) {
              onSelectAllFilters();
            } else {
              onClearFilters();
            }
          }}
          onSelect={(event) => event.preventDefault()}
          className={cn(changedCount === 0 && 'text-muted-foreground')}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate">
              {t('repository.filters.changed', { defaultValue: 'Changed' })}
            </span>
          </span>
          <span className="ml-auto shrink-0 tabular-nums text-xs text-muted-foreground">
            {changedCount}
          </span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {filters.map((filter) => {
          const isEmptyFilter = filter.count === 0;
          return (
            <DropdownMenuCheckboxItem
              key={filter.id}
              checked={activeFilters.has(filter.id)}
              onCheckedChange={() => onToggleFilter(filter.id)}
              onSelect={(event) => event.preventDefault()}
              className={cn('gap-2', isEmptyFilter && 'text-muted-foreground')}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                {filter.icon && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {filter.icon}
                  </span>
                )}
                <span className="truncate">{filter.label}</span>
              </span>
              <span className="ml-auto shrink-0 tabular-nums text-xs text-muted-foreground">
                {filter.count}
              </span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ChangeFilterEmptyStateProps {
  activeFilters: ReadonlySet<RepositoryChangeFacet>;
  searchText: string;
  onShowAll: () => void;
}

const ChangeFilterEmptyState: React.FC<ChangeFilterEmptyStateProps> = ({
  activeFilters,
  searchText,
  onShowAll,
}) => {
  const { t } = useTranslation('process');
  const labelByFilter: Record<RepositoryChangeFacet, string> = {
    new: t('repository.filters.new', { defaultValue: 'New' }),
    modified: t('repository.filters.modified', { defaultValue: 'Modified' }),
    conflict: t('repository.filters.conflict', { defaultValue: 'Conflicts' }),
    deleted: t('repository.filters.deleted', { defaultValue: 'Deleted' }),
  };
  const selectedLabels = changeFilterFacets
    .filter((filter) => activeFilters.has(filter))
    .map((filter) => labelByFilter[filter].toLowerCase());
  const selectedLabel =
    selectedLabels.length === 1
      ? selectedLabels[0]
      : t('repository.filters.selectedSet', {
          count: selectedLabels.length,
          defaultValue: 'selected',
        });
  const onlyDeletedSelected = activeFilters.size === 1 && activeFilters.has('deleted');

  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center px-5 py-6 text-center">
      <div className="mb-1 text-sm font-medium text-foreground">
        {searchText
          ? t('repository.filters.emptySearchTitle', {
              filter: selectedLabel,
              defaultValue: `No ${selectedLabel} procedures match this search`,
            })
          : t('repository.filters.emptyTitle', {
              filter: selectedLabel,
              defaultValue: `No ${selectedLabel} procedures`,
            })}
      </div>
      <div className="mb-3 max-w-[220px] text-xs text-muted-foreground">
        {onlyDeletedSelected
          ? t('repository.filters.emptyDeletedDescription', {
              defaultValue:
                'Pull from the repository to detect external deletions, or delete a bound canvas step to queue a repository delete.',
            })
          : t('repository.filters.emptyDescription', {
              defaultValue:
                'Changes will appear here after you edit repository-backed steps on the canvas.',
            })}
      </div>
      <Button variant="ghost" size="sm" onClick={onShowAll}>
        {t('repository.filters.showAll', { defaultValue: 'Show all' })}
      </Button>
    </div>
  );
};

interface DeletedRepositoryItemProps {
  record: DeletedRepositoryRecord;
}

const DeletedRepositoryItem: React.FC<DeletedRepositoryItemProps> = ({ record }) => {
  const { t } = useTranslation('process');
  const name = readRecordName(record.data, record.recordId);
  const sourceLabel =
    record.source === 'remote'
      ? t('repository.changeStatus.deletedRemote', { defaultValue: 'Deleted remotely' })
      : t('repository.changeStatus.deletedLocal', { defaultValue: 'Pending delete' });

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-800">
      <Trash2 className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-sm line-through" title={name}>
        {name}
      </span>
      <span className="shrink-0 rounded border border-red-200 bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
        {sourceLabel}
      </span>
    </div>
  );
};

interface ProcessTreeItemProps {
  node: ProcessTreeNode;
  effectiveData: Record<string, unknown>;
  isExpanded: boolean;
  isOnCanvas: boolean;
  changeStatus: RecordChangeStatus;
  onToggle: () => void;
  onSelect: () => void;
  onDragStart: (e: React.DragEvent) => void;
  readOnly?: boolean;
}

const ProcessTreeItem: React.FC<ProcessTreeItemProps> = ({
  node,
  effectiveData,
  isExpanded,
  isOnCanvas,
  changeStatus,
  onToggle,
  onSelect,
  onDragStart,
  readOnly = false,
}) => {
  const { t } = useTranslation('process');
  const { children, depth } = node;
  const hasChildren = children.length > 0;
  const indent = depth * 16;
  const isDeleted = changeStatus === 'deleted';

  // Use effective values (with local changes applied)
  const effectiveName = (effectiveData.Name ?? effectiveData.name ?? node.record.name) as string;
  const effectiveStatus = (effectiveData.Status ?? effectiveData.status ?? node.record.status) as string | undefined;

  const statusColor = useMemo(() => {
    const status = effectiveStatus?.toLowerCase();
    if (!status) return null;
    if (status === 'approved' || status === 'active') return 'bg-green-500';
    if (status === 'draft') return 'bg-yellow-500';
    if (status === 'review' || status === 'pending') return 'bg-blue-500';
    if (status === 'deprecated' || status === 'inactive') return 'bg-gray-400';
    return 'bg-gray-400';
  }, [effectiveStatus]);

  return (
    <div
      className={cn(
        'flex items-center gap-1 px-2 py-1.5 hover:bg-muted/50 cursor-pointer transition-colors group',
        isOnCanvas && !changeStatus && 'bg-muted/30',
        changeStatus === 'modified' && 'bg-amber-50 hover:bg-amber-100/70',
        changeStatus === 'new' && 'bg-green-50 hover:bg-green-100/70',
        changeStatus === 'conflict' && 'bg-red-50 hover:bg-red-100/70',
        isDeleted && 'bg-red-50 text-red-800 hover:bg-red-100/70'
      )}
      style={{ paddingLeft: `${8 + indent}px` }}
      onClick={onSelect}
      onDoubleClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSelect();
        if (e.key === ' ' && hasChildren) {
          e.preventDefault();
          onToggle();
        }
      }}
      draggable={!readOnly && !isDeleted}
      onDragStart={readOnly || isDeleted ? undefined : onDragStart}
      role="treeitem"
      tabIndex={0}
      aria-expanded={hasChildren ? isExpanded : undefined}
    >
      {/* Expand/collapse button */}
      <button
        type="button"
        className={cn(
          'h-5 w-5 flex items-center justify-center rounded hover:bg-muted transition-colors',
          !hasChildren && 'invisible'
        )}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        tabIndex={-1}
      >
        {hasChildren &&
          (isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ))}
      </button>

      {/* Status indicator */}
      {statusColor && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn('h-2 w-2 rounded-full flex-shrink-0', statusColor)} />
            </TooltipTrigger>
            <TooltipContent>{effectiveStatus}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Name (shows effective value with local changes applied).
         `min-w-0` is required on a flex child for `truncate` to work
         — flex items default to min-width: auto = content width,
         which would make the row push past the panel and skip
         the ellipsis entirely. */}
      <span
        className={cn(
          'text-sm text-foreground truncate flex-1 min-w-0',
          isDeleted && 'text-red-800 line-through'
        )}
        title={effectiveName}
      >
        {effectiveName}
      </span>

      {/* Change status indicator (modified/new/conflict) */}
      {changeStatus && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'h-5 w-5 flex items-center justify-center',
                  changeStatus === 'modified' && 'text-amber-500',
                  changeStatus === 'new' && 'text-green-500',
                  changeStatus === 'conflict' && 'text-red-500',
                  isDeleted && 'text-red-600'
                )}
              >
                {changeStatus === 'modified' && <Pencil className="h-3.5 w-3.5" />}
                {changeStatus === 'new' && <Plus className="h-4 w-4" />}
                {changeStatus === 'conflict' && <AlertTriangle className="h-4 w-4" />}
                {isDeleted && <Trash2 className="h-4 w-4" />}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {changeStatus === 'modified' && t('repository.changeStatus.modified')}
              {changeStatus === 'new' && t('repository.changeStatus.new')}
              {changeStatus === 'conflict' && t('repository.changeStatus.conflict')}
              {isDeleted &&
                t('repository.changeStatus.deleted', { defaultValue: 'Pending repository deletion' })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* On canvas indicator */}
      {isOnCanvas && !changeStatus && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="h-5 w-5 flex items-center justify-center text-green-600">
                <Check className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>{t('repository.alreadyOnCanvas')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

interface ProcessImpactPanelProps {
  impact: CompassProcessImpact;
}

const ProcessImpactPanel: React.FC<ProcessImpactPanelProps> = ({ impact }) => {
  const { t } = useTranslation('process');
  const replacementImpact = impact.replacementImpact;
  return (
    <div className="border-t border-border bg-muted/20 p-3 space-y-3">
      <div>
        <div className="text-xs font-medium text-muted-foreground">{t('repository.impact.graphContext')}</div>
        <div className="text-sm font-semibold text-foreground">{impact.name}</div>
        {impact.description && (
          <div className="text-xs text-muted-foreground mt-1">{impact.description}</div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <ImpactMetric
          icon={<GitBranch className="h-3.5 w-3.5" />}
          label={t('repository.impact.dependsOn')}
          value={impact.dependsOn.length}
        />
        <ImpactMetric
          icon={<GitBranch className="h-3.5 w-3.5" />}
          label={t('repository.impact.dependents')}
          value={impact.dependedOnBy.length}
        />
        <ImpactMetric
          icon={<ChevronRight className="h-3.5 w-3.5" />}
          label={t('repository.impact.children')}
          value={impact.childProcessIds.length}
        />
        <ImpactMetric
          icon={<Link2 className="h-3.5 w-3.5" />}
          label={t('repository.impact.usedInDiagrams')}
          value={impact.usedInDiagrams.length}
        />
      </div>

      <div className="rounded border border-border bg-background px-2 py-2 text-xs space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 font-medium text-foreground">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <span>{t('repository.impact.replacementTitle')}</span>
          </div>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[11px] font-medium',
              replacementImpact.readiness === 'blocked' && 'bg-red-50 text-red-700',
              replacementImpact.readiness === 'review_needed' && 'bg-amber-50 text-amber-700',
              replacementImpact.readiness === 'ready' && 'bg-green-50 text-green-700',
            )}
          >
            {t(`repository.impact.replacementReadiness.${replacementImpact.readiness}`)}
          </span>
        </div>
        <div className="text-muted-foreground">
          {t('repository.impact.replacementSummary', {
            count: replacementImpact.directImpactCount,
          })}
        </div>
        <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-[11px]">
          <ImpactSignalCount
            label={t('repository.impact.signal.roles')}
            value={replacementImpact.relatedSignals.roles.length}
          />
          <ImpactSignalCount
            label={t('repository.impact.signal.positions')}
            value={replacementImpact.relatedSignals.atlasPositions.length}
          />
          <ImpactSignalCount
            label={t('repository.impact.signal.work')}
            value={replacementImpact.relatedSignals.activeWorkItems.length}
          />
          <ImpactSignalCount
            label={t('repository.impact.signal.systems')}
            value={replacementImpact.relatedSignals.systems.length}
          />
          <ImpactSignalCount
            label={t('repository.impact.signal.controls')}
            value={replacementImpact.relatedSignals.controls.length}
          />
        </div>
        {replacementImpact.blockers.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {replacementImpact.blockers.map((blocker) => (
              <span
                key={blocker}
                className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"
              >
                {t(`repository.impact.blocker.${blocker}`)}
              </span>
            ))}
          </div>
        )}
        {replacementImpact.gaps.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {replacementImpact.gaps.slice(0, 4).map((gap) => (
              <span
                key={gap.kind}
                className={cn(
                  'rounded px-1.5 py-0.5 text-[11px]',
                  gap.severity === 'warning'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {t(`repository.impact.gap.${gap.kind}`)}
              </span>
            ))}
          </div>
        )}
      </div>

      {impact.usedInDiagrams.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">{t('repository.impact.usedIn')}</div>
          <div className="space-y-1">
            {impact.usedInDiagrams.slice(0, 4).map((diagram) => (
              <div
                key={diagram.diagramId}
                className="rounded border border-border bg-background px-2 py-1 text-xs"
              >
                <div className="font-medium text-foreground">{diagram.name}</div>
                <div className="text-muted-foreground">{diagram.type}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ImpactMetric: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
}> = ({ icon, label, value }) => (
  <div className="rounded border border-border bg-background px-2 py-1.5">
    <div className="flex items-center gap-1 text-muted-foreground">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-sm font-semibold text-foreground">{value}</div>
  </div>
);

const ImpactSignalCount: React.FC<{
  label: string;
  value: number;
}> = ({ label, value }) => (
  <div className="min-w-0 text-muted-foreground">
    <span className="truncate">{label}</span>{' '}
    <span className="font-semibold text-foreground">{value}</span>
  </div>
);
